import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
    UploadSimple, ArrowsClockwise, CheckCircle, Warning, Fingerprint, FloppyDisk,
    Pencil, ClockCounterClockwise, Trash, Lock,
} from "@phosphor-icons/react";

function currentMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Pretty format YYYY-MM → "June 2026"
function fmtMonth(m) {
    if (!m || !/^\d{4}-\d{2}$/.test(m)) return m || "";
    const [y, mo] = m.split("-").map(Number);
    return new Date(y, mo - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
}

export default function Biometric() {
    const [month, setMonth] = useState(currentMonth());
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snapshots, setSnapshots] = useState([]);
    const [cfg, setCfg] = useState({ standard_daily_hours: 10 });
    const [cfgOpen, setCfgOpen] = useState(false);
    const [saveResult, setSaveResult] = useState(null);
    const [cfgDraft, setCfgDraft] = useState(10);
    const [editing, setEditing] = useState(null); // snapshot being edited
    const [deleting, setDeleting] = useState(null); // snapshot pending delete confirmation
    const [deleteBusy, setDeleteBusy] = useState(false);

    const loadCfg = useCallback(async () => {
        try {
            const { data } = await api.get("/biometric/config");
            setCfg(data);
            setCfgDraft(data.standard_daily_hours);
        } catch (e) { /* not critical */ }
    }, []);

    const loadSnapshots = useCallback(async () => {
        try {
            const { data } = await api.get("/biometric/snapshots", { params: { month } });
            setSnapshots(data);
        } catch (e) { toast.error(formatApiError(e)); }
    }, [month]);

    useEffect(() => { loadCfg(); }, [loadCfg]);
    useEffect(() => { loadSnapshots(); }, [loadSnapshots]);

    const upload = async () => {
        if (!file) return toast.error("Pick the .xls / .xlsx from your fingerprint machine first");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            // Send the picker's month as a hint — backend rejects if the file's
            // Report Month doesn't match, so we can never silently mis-attribute.
            const { data } = await api.post(`/biometric/preview?month=${month}`, fd,
                { headers: { "Content-Type": "multipart/form-data" } });
            setPreview(data);
            setRows(data.rows.map((r) => ({ ...r, _selected: r.tier_ok })));
            // Adopt the file's detected month for the picker + snapshot list.
            if (data.detected_month && data.detected_month !== month) {
                setMonth(data.detected_month);
            }
            toast.success(
                `Parsed ${fmtMonth(data.detected_month)} · ${data.counts.matched + data.counts.unmatched} employees · ${data.counts.matched_labour} labour matched`
            );
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const setRow = (i, patch) => setRows((rr) => rr.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

    const save = async () => {
        const chosen = rows.filter((r) => r._selected && r.tier_ok);
        if (chosen.length === 0) return toast.error("Nothing to save — pick at least one labour row");
        setSaving(true);
        try {
            const { data } = await api.post("/biometric/save", {
                month: preview.month,
                file_hash: preview.file_hash,
                file_name: preview.file_name,
                rows: chosen.map((r) => ({
                    punch_code: r.punch_code, employee_id: r.employee_id,
                    worked_working_days: r.worked_working_days,
                    lop_days: r.lop_days,
                    ot_hours: Number(r.ot_hours) || 0,
                    absent_days: r.absent_days, wo_days: r.wo_days,
                    hl_days: r.hl_days, lv_days: r.lv_days,
                    work_hours_total: r.work_hours_total,
                    present_full: r.present_full, present_half: r.present_half,
                })),
            });
            const added = data.added?.length || 0;
            const corrected = data.corrected?.length || 0;
            const dupes = data.duplicates?.length || 0;
            const locked = data.locked?.length || 0;
            // Always open the summary dialog so managers see exactly what happened.
            setSaveResult({ ...data, month: preview.month });
            if (added || corrected) {
                toast.success(
                    `Saved ${added} new · ${corrected} corrected` +
                    (dupes ? ` · ${dupes} duplicates skipped` : "") +
                    (locked ? ` · ${locked} locked` : "")
                );
            }
            setPreview(null); setRows([]); setFile(null);
            loadSnapshots();
        } catch (e) {
            // 409 with structured detail means "everything was a duplicate or
            // locked". Show the summary dialog so the manager can see who was
            // skipped and why.
            const detail = e?.response?.data?.detail;
            if (e?.response?.status === 409 && typeof detail === "object") {
                setSaveResult({ ...detail, all_skipped: true });
                toast.warning(detail.message || "All rows already exist in the system.");
            } else {
                toast.error(formatApiError(e));
            }
        }
        finally { setSaving(false); }
    };

    const saveCfg = async () => {
        try {
            const { data } = await api.put("/biometric/config", { standard_daily_hours: Number(cfgDraft) });
            setCfg(data);
            setCfgOpen(false);
            toast.success(`Standard shift saved: ${data.standard_daily_hours} hr/day`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const deleteSnap = async () => {
        if (!deleting) return;
        setDeleteBusy(true);
        try {
            const { data } = await api.delete(`/biometric/snapshots/id/${deleting.id}`);
            toast.success(`Deleted ${data.deleted}'s ${data.month} snapshot`);
            setDeleting(null);
            loadSnapshots();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDeleteBusy(false); }
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Biometric attendance"
                sub="Drop the monthly 'Month Performance' export from the fingerprint machine. Report month is trusted from the file, OT derived from your standard shift."
                right={
                    <div className="flex items-center gap-2">
                        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="bio-month" />
                        <Button variant="outline" onClick={() => setCfgOpen(true)} data-testid="bio-cfg">
                            <ClockCounterClockwise size={14} className="mr-2" /> {cfg.standard_daily_hours} hr shift
                        </Button>
                        <Button variant="outline" onClick={loadSnapshots} data-testid="bio-refresh">
                            <ArrowsClockwise size={14} className="mr-2" /> Refresh
                        </Button>
                    </div>
                } />

            <div className="p-8 space-y-6">
                {/* Upload panel */}
                <div className="bg-white border rounded-md p-5" style={{ borderColor: "var(--se-border)" }}>
                    <div className="font-serif text-lg mb-2 flex items-center gap-2">
                        <Fingerprint size={16} /> Upload monthly performance file
                    </div>
                    <div className="text-xs text-stone-500 mb-4">
                        Report month is read from the file itself — if the file is for June 2026 that&apos;s what we save it under, regardless of what the picker shows. Same file can&apos;t be uploaded twice; delete that month&apos;s snapshots first if you need to redo.
                    </div>
                    <div className="flex items-end gap-3">
                        <div className="flex-1">
                            <Label>File</Label>
                            <Input type="file" accept=".xls,.xlsx"
                                   onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="bio-file" />
                        </div>
                        <Button onClick={upload} disabled={busy || !file}
                                style={{ background: "#f59e0b", color: "#fff" }} data-testid="bio-upload">
                            <UploadSimple size={14} className="mr-2" /> {busy ? "Parsing..." : "Parse & preview"}
                        </Button>
                    </div>
                </div>

                {/* Preview */}
                {preview ? (
                    <>
                        <div className="border rounded-md p-4" style={{ borderColor: "#bfdbfe", background: "#eff6ff" }}>
                            <div className="text-sm text-blue-900 flex items-center gap-2">
                                <CheckCircle size={14} /> File is for <b>{fmtMonth(preview.detected_month)}</b> · standard shift <b>{preview.standard_daily_hours} hr/day</b> · saving will attribute to <b>{preview.month}</b>
                            </div>
                        </div>
                        {preview.unmatched?.length > 0 ? (
                            <div className="border rounded-md p-4" style={{ borderColor: "#fecaca", background: "#fef2f2" }} data-testid="bio-unmatched">
                                <div className="text-sm font-semibold text-red-800 flex items-center gap-2">
                                    <Warning size={14} /> {preview.unmatched.length} punch code(s) don&apos;t match any employee
                                </div>
                                <div className="mt-2 text-xs text-red-800 space-y-1">
                                    {preview.unmatched.slice(0, 10).map((u) => (
                                        <div key={u.punch_code}>
                                            <span className="font-mono">{u.punch_code}</span> · {u.punch_name} · WD {u.worked_working_days} · A {u.absent_days} · Work {u.work_hours_total}h
                                        </div>
                                    ))}
                                    {preview.unmatched.length > 10 ? <div>…and {preview.unmatched.length - 10} more.</div> : null}
                                </div>
                                <div className="mt-3 text-xs text-red-800">
                                    Fix by setting the <strong>Biometric punch code</strong> field on each employee in <a href="/employees" className="underline">Employees</a>, then re-upload.
                                </div>
                            </div>
                        ) : null}

                        <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                                <div className="font-serif text-lg">
                                    Preview — {fmtMonth(preview.month)} · {preview.counts.matched_labour} labour to save
                                    {preview.counts.matched_non_labour > 0 ? <span className="text-xs text-stone-500 ml-2">({preview.counts.matched_non_labour} non-labour rows are shown but not saved)</span> : null}
                                </div>
                                <Button onClick={save} disabled={saving} style={{ background: "#f59e0b", color: "#fff" }} data-testid="bio-save">
                                    <FloppyDisk size={13} className="mr-1" /> {saving ? "Saving..." : "Save & feed payroll"}
                                </Button>
                            </div>
                            <table className="se-table" data-testid="bio-preview-table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Employee</th><th>Punch</th><th>Tier</th>
                                        <th className="text-right">P full / half</th>
                                        <th className="text-right">Absent (=LOP)</th>
                                        <th className="text-right">WO / HL / LV</th>
                                        <th className="text-right">Work hrs</th>
                                        <th className="text-right">Expected</th>
                                        <th className="text-right">OT hrs (edit)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={r.punch_code} data-testid={`bio-row-${r.punch_code}`}
                                            className={r.tier_ok ? "" : "bg-stone-50"}>
                                            <td>
                                                <input type="checkbox" checked={!!r._selected}
                                                    onChange={() => setRow(i, { _selected: !r._selected })}
                                                    disabled={!r.tier_ok}
                                                    data-testid={`bio-sel-${r.punch_code}`} />
                                            </td>
                                            <td>{r.employee_name}
                                                <div className="text-[10px] text-stone-500">{r.employee_code}{r.department ? ` · ${r.department}` : ""}</div>
                                            </td>
                                            <td className="font-mono text-xs">{r.punch_code}</td>
                                            <td className="text-xs">
                                                {r.tier_ok
                                                    ? <span className="text-emerald-700">labour</span>
                                                    : <span className="text-stone-500">{r.tier} — skipped</span>}
                                            </td>
                                            <td className="se-num text-right text-xs">{r.present_full} / {r.present_half}</td>
                                            <td className="se-num text-right text-xs text-amber-700">
                                                {r.absent_days}
                                                {r.short_shift_days > 0 ? (
                                                    <div className="text-[10px] text-rose-600"
                                                         title={`${r.short_shift_days} day(s) were flagged Present by the machine but worked less than ${r.min_hours_threshold} hrs — reclassified as Absent for payroll.`}>
                                                        incl. {r.short_shift_days} short shift{r.short_shift_days > 1 ? "s" : ""}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td className="se-num text-right text-xs">{r.wo_days} / {r.hl_days} / {r.lv_days}</td>
                                            <td className="se-num text-right text-xs" title={`Sum of per-day WORK column · machine's Total OT summary: ${r.ot_hours_summary}`}>
                                                {r.work_hours_total}
                                                {r.work_hours_source === "summary"
                                                    ? <div className="text-[10px] text-amber-600" title="Per-day WORK column in the file was broken (mostly 00:00). Falling back to the summary Tot. Work+OT cell.">
                                                        from summary
                                                      </div>
                                                    : null}
                                                <div className="text-[10px] text-stone-500">+OT total: {r.work_plus_ot_hours}</div>
                                            </td>
                                            <td className="se-num text-right text-xs" title="present_full × standard + present_half × standard/2">
                                                {r.expected_hours}
                                            </td>
                                            <td className="text-right">
                                                <Input type="number" step="0.25" value={r.ot_hours} className="w-20 text-right text-xs h-8"
                                                    onChange={(e) => setRow(i, { ot_hours: e.target.value })}
                                                    disabled={!r.tier_ok}
                                                    data-testid={`bio-ot-${r.punch_code}`} />
                                                {r.ot_hours_machine !== Number(r.ot_hours)
                                                    ? <div className="text-[10px] text-stone-500">derived: {r.ot_hours_machine}</div>
                                                    : null}
                                                {r.ot_hours_summary != null && Math.abs(r.ot_hours_summary - r.ot_hours_machine) > 0.01
                                                    ? <div className="text-[10px] text-amber-600" title="Machine's own 'Total OT' summary row — for reconciliation only">
                                                        machine reported: {r.ot_hours_summary}
                                                      </div>
                                                    : null}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : null}

                {/* Existing snapshots */}
                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <div className="px-5 py-3 border-b font-serif text-lg" style={{ borderColor: "var(--se-border)" }}>
                        <CheckCircle size={14} className="inline mr-2 text-emerald-600" />
                        Saved snapshots · {fmtMonth(month)} · {snapshots.length}
                    </div>
                    {snapshots.length === 0 ? (
                        <div className="p-8 text-center text-sm text-stone-500">No snapshot saved for {fmtMonth(month)} yet.</div>
                    ) : (
                        <table className="se-table" data-testid="bio-snapshots-table">
                            <thead>
                                <tr>
                                    <th>Employee</th><th>Punch</th>
                                    <th className="text-right">Worked WD</th>
                                    <th className="text-right">LOP</th>
                                    <th className="text-right">OT hrs</th>
                                    <th className="text-right">Work hrs</th>
                                    <th>Saved</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {snapshots.map((s) => (
                                    <tr key={s.id} data-testid={`bio-snap-${s.id}`}>
                                        <td>{s.employee_name} <span className="text-[10px] text-stone-500">({s.employee_code})</span>
                                            {s.manually_edited ? <span className="ml-1 se-chip se-chip-teal" title="Manually edited after upload">edited</span> : null}
                                            {s.is_locked ? <span className="ml-1 se-chip se-chip-posted" title={s.lock_reason || "Payroll already posted"}><Lock size={9} className="inline mr-0.5" />locked</span> : null}
                                        </td>
                                        <td className="font-mono text-xs">{s.punch_code}</td>
                                        <td className="se-num text-right text-xs">{s.worked_working_days}</td>
                                        <td className="se-num text-right text-xs text-amber-700">{s.lop_days}</td>
                                        <td className="se-num text-right text-xs">{s.ot_hours}</td>
                                        <td className="se-num text-right text-xs">{s.work_hours_total}</td>
                                        <td className="text-[10px] text-stone-500">{(s.updated_at || s.created_at || "").slice(0, 10)}</td>
                                        <td className="text-right whitespace-nowrap">
                                            <Button size="sm" variant="outline" onClick={() => setEditing(s)}
                                                    disabled={s.is_locked}
                                                    title={s.is_locked ? s.lock_reason : "Edit this snapshot"}
                                                    data-testid={`bio-edit-${s.id}`}>
                                                <Pencil size={12} className="mr-1" /> Edit
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => setDeleting(s)}
                                                    disabled={s.is_locked}
                                                    title={s.is_locked ? s.lock_reason : "Delete this snapshot"}
                                                    className="ml-2"
                                                    style={{ borderColor: "#fecaca", color: s.is_locked ? undefined : "#b91c1c" }}
                                                    data-testid={`bio-del-${s.id}`}>
                                                <Trash size={12} className="mr-1" /> Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Standard-hours config dialog */}
            <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
                <DialogContent data-testid="bio-cfg-dialog">
                    <DialogHeader><DialogTitle>Standard shift length</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Working hours per day</Label>
                            <Input type="number" step="0.25" min="1" max="24"
                                value={cfgDraft}
                                onChange={(e) => setCfgDraft(e.target.value)}
                                data-testid="bio-cfg-hrs" />
                            <div className="text-[10px] text-stone-500 mt-1">
                                Used to derive OT for every biometric upload — Expected = present_full × {cfgDraft || "?"} + present_half × {cfgDraft ? cfgDraft / 2 : "?"}. Anything the employee clocked beyond Expected becomes OT.
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCfgOpen(false)}>Cancel</Button>
                        <Button onClick={saveCfg} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="bio-cfg-save">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Snapshot edit dialog */}
            <SnapshotEditDialog
                snap={editing}
                onClose={() => setEditing(null)}
                onSaved={() => { setEditing(null); loadSnapshots(); }}
            />

            {/* Save-summary dialog: shows what happened after a biometric upload */}
            <SaveSummaryDialog result={saveResult} onClose={() => setSaveResult(null)} />

            {/* Snapshot delete confirm */}
            <Dialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
                <DialogContent data-testid="bio-del-dialog">
                    <DialogHeader><DialogTitle>Delete snapshot?</DialogTitle></DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div>
                            You&apos;re about to remove <b>{deleting?.employee_name}</b>&apos;s snapshot for <b>{fmtMonth(deleting?.month)}</b>.
                            Historical journal entries stay intact — only the biometric snapshot goes away.
                        </div>
                        <div className="text-xs text-stone-500">
                            If this employee&apos;s payroll for {fmtMonth(deleting?.month)} is still a draft, running &quot;Prepare payroll&quot; again will fall back to manual attendance until you upload a new file.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleting(null)} disabled={deleteBusy}>Cancel</Button>
                        <Button onClick={deleteSnap} disabled={deleteBusy}
                                style={{ background: "#dc2626", color: "#fff" }}
                                data-testid="bio-del-confirm">
                            {deleteBusy ? "Deleting..." : "Delete snapshot"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function SnapshotEditDialog({ snap, onClose, onSaved }) {
    const [form, setForm] = useState({});
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (snap) {
            setForm({
                worked_working_days: snap.worked_working_days,
                lop_days: snap.lop_days,
                ot_hours: snap.ot_hours,
                absent_days: snap.absent_days,
                wo_days: snap.wo_days,
                hl_days: snap.hl_days,
                lv_days: snap.lv_days,
                work_hours_total: snap.work_hours_total,
                present_full: snap.present_full,
                present_half: snap.present_half,
            });
            setNote("");
        }
    }, [snap]);
    if (!snap) return null;

    const submit = async () => {
        setSaving(true);
        try {
            const changes = {};
            for (const k of Object.keys(form)) {
                const v = form[k];
                if (v === "" || v == null) continue;
                if (Number(v) !== Number(snap[k])) changes[k] = Number(v);
            }
            if (Object.keys(changes).length === 0) {
                toast.info("No changes to save");
                setSaving(false);
                return;
            }
            if (note.trim()) changes.edit_note = note.trim();
            await api.patch(`/biometric/snapshots/${snap.id}`, changes);
            toast.success(`Updated ${snap.employee_name}'s ${snap.month} snapshot`);
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const field = (key, label, step = "1") => (
        <div>
            <Label>{label}</Label>
            <Input type="number" step={step}
                value={form[key] ?? ""}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                data-testid={`bio-edit-${key}`} />
        </div>
    );

    return (
        <Dialog open={!!snap} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="bio-edit-dialog" className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Edit snapshot — {snap.employee_name} · {snap.month}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    {field("worked_working_days", "Worked working days", "0.5")}
                    {field("lop_days", "LOP days", "0.5")}
                    {field("ot_hours", "OT hours", "0.25")}
                    {field("work_hours_total", "Work hours total", "0.25")}
                    {field("present_full", "Present full days", "1")}
                    {field("present_half", "Present half days", "1")}
                    {field("absent_days", "Absent days", "1")}
                    {field("wo_days", "Weekly off days", "1")}
                    {field("hl_days", "Holiday days", "1")}
                    {field("lv_days", "Leave days", "1")}
                </div>
                <div className="mt-2">
                    <Label>Edit note (why)</Label>
                    <Input value={note} onChange={(e) => setNote(e.target.value)}
                        placeholder="e.g. corrected LOP after Ram brought late slip"
                        data-testid="bio-edit-note" />
                </div>
                {snap.edit_history?.length ? (
                    <div className="mt-3 border-t pt-3">
                        <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Edit history</div>
                        <div className="text-xs space-y-1 max-h-32 overflow-auto">
                            {snap.edit_history.slice().reverse().map((h, idx) => (
                                <div key={idx} className="text-stone-600">
                                    <span className="text-stone-400">{h.at?.slice(0, 16).replace("T", " ")}</span>
                                    {" · "}<span className="font-medium">{h.by_name || h.by}</span>
                                    {h.note ? <span className="italic"> — {h.note}</span> : null}
                                    <div className="text-[10px] text-stone-500 ml-2">
                                        {Object.entries(h.diff || {}).map(([k, v]) => `${k}: ${v.from} → ${v.to}`).join(" · ")}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="bio-edit-save">
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SaveSummaryDialog({ result, onClose }) {
    if (!result) return null;
    const added = result.added || [];
    const corrected = result.corrected || [];
    const duplicates = result.duplicates || [];
    const locked = result.locked || [];
    const nothingChanged = added.length === 0 && corrected.length === 0;
    return (
        <Dialog open={!!result} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="bio-save-summary">
                <DialogHeader>
                    <DialogTitle>
                        Upload summary — {fmtMonth(result.month)}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                    {nothingChanged ? (
                        <div className="rounded-sm border p-3 text-amber-800"
                             style={{ borderColor: "#fde68a", background: "#fffbeb" }}
                             data-testid="bio-save-nothing">
                            {result.message || "All rows in this file already exist in the system with the same values. Nothing changed."}
                        </div>
                    ) : null}

                    <Bucket
                        title={`New saved · ${added.length}`}
                        tone="emerald"
                        items={added}
                        testid="bio-summary-added"
                    />
                    <Bucket
                        title={`Corrections applied · ${corrected.length}`}
                        tone="sky"
                        items={corrected}
                        testid="bio-summary-corrected"
                        sub="Existing snapshot values were updated for these employees."
                    />
                    <Bucket
                        title={`Duplicates skipped · ${duplicates.length}`}
                        tone="stone"
                        items={duplicates}
                        testid="bio-summary-duplicates"
                        sub="These employees already had identical values on record — nothing was changed."
                    />
                    <Bucket
                        title={`Locked (payroll already posted) · ${locked.length}`}
                        tone="rose"
                        items={locked}
                        testid="bio-summary-locked"
                        sub="Their payroll is already posted, so their attendance can't be edited. Reverse the payroll first if a correction is required."
                        showReason
                    />
                </div>
                <DialogFooter>
                    <Button onClick={onClose} data-testid="bio-summary-close"
                            style={{ background: "var(--se-brand)", color: "#fff" }}>
                        Got it
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Bucket({ title, tone, items, sub, testid, showReason }) {
    if (!items || items.length === 0) return null;
    const palette = {
        emerald: { bg: "#ecfdf5", border: "#a7f3d0", text: "#065f46" },
        sky:     { bg: "#eff6ff", border: "#bfdbfe", text: "#1e3a8a" },
        stone:   { bg: "#fafaf9", border: "#e7e5e4", text: "#44403c" },
        rose:    { bg: "#fff1f2", border: "#fecdd3", text: "#9f1239" },
    }[tone || "stone"];
    return (
        <div className="rounded-sm border p-3"
             style={{ borderColor: palette.border, background: palette.bg, color: palette.text }}
             data-testid={testid}>
            <div className="font-medium">{title}</div>
            {sub ? <div className="text-[11px] opacity-80 mt-1">{sub}</div> : null}
            <ul className="text-xs mt-2 space-y-0.5">
                {items.map((it, i) => (
                    <li key={i}>
                        · {it.employee_name}{it.employee_code ? ` (${it.employee_code})` : ""}
                        {showReason && it.reason ? <span className="opacity-70"> — {it.reason}</span> : null}
                    </li>
                ))}
            </ul>
        </div>
    );
}

